/**
 * Hand-rolled SVG chart primitives.
 *
 * No chart library: the shapes needed here are simple, and a dependency would
 * bring its own palette and mark defaults that fight the ones chosen here.
 *
 * Conventions held throughout: one y-axis per plot (never two scales), solid
 * hairline grid, thin marks, a legend whenever more than one series is drawn,
 * and tooltips that enhance rather than gate — every value is also reachable
 * from the table view.
 */

import { useCallback, useMemo, useRef, useState, type ReactNode } from 'react';

const PAD = { top: 10, right: 12, bottom: 24, left: 44 };

export interface Series {
  label: string;
  color: string;
  points: { x: number; y: number | null }[];
  /** Rendered in the tooltip after the value. */
  unit: string;
  /**
   * Measured occasionally rather than every cycle (speed tests run every few
   * hours, not every minute).
   *
   * For a continuously-sampled metric a null means "we lost contact", and
   * breaking the line there is the honest rendering — that principle is why
   * the path splits on nulls at all. But an hourly rollup of a metric that is
   * only sampled every few hours is null for most hours simply because no test
   * was scheduled, not because anything was missed. Breaking on those nulls
   * shatters the line into disconnected stubs (one per adjacent pair that
   * happened to land in consecutive buckets), which is what the speed chart
   * did on any range longer than 24h.
   *
   * So for a sparse series the nulls are dropped and consecutive measurements
   * are joined, with a dot on each so the reader can still see the samples are
   * discrete and occasional.
   */
  sparse?: boolean;
}

function niceTicks(min: number, max: number, count = 4): number[] {
  if (!Number.isFinite(min) || !Number.isFinite(max) || min === max) return [min || 0];
  const span = max - min;
  const rawStep = span / count;
  const mag = 10 ** Math.floor(Math.log10(rawStep));
  const step = [1, 2, 2.5, 5, 10].map((m) => m * mag).find((s) => s >= rawStep) ?? mag * 10;
  const out: number[] = [];
  for (let v = Math.ceil(min / step) * step; v <= max + 1e-9; v += step) out.push(Number(v.toFixed(6)));
  return out;
}

const fmtTime = (ts: number, spanMs: number): string =>
  new Date(ts).toLocaleString(undefined, {
    hour: '2-digit',
    minute: '2-digit',
    ...(spanMs > 36 * 3_600_000 ? { month: 'short', day: 'numeric' } : {}),
  });

export function LineChart({
  series,
  height = 190,
  yLabel,
  yMin = 0,
}: {
  series: Series[];
  height?: number;
  yLabel?: string;
  /** Force the axis to start at zero unless the data demands otherwise. */
  yMin?: number;
}) {
  // Cursor position in plot coordinates, plus the time under it. NOT an index
  // into series[0]: the series can have different lengths and different null
  // patterns (a speed test can record a download and fail the upload), so one
  // shared index silently reads the wrong series' value.
  const [hover, setHover] = useState<{ x: number; t: number; px: number } | null>(null);

  // The viewBox must match the rendered pixel width, or the mouse never lines
  // up with the crosshair. With a fixed viewBox (720) and width="100%", the
  // default preserveAspectRatio ("xMidYMid meet") scales the content to FIT and
  // then CENTRES it, so on a wider container the drawing sits in a letterboxed
  // band with dead margins either side — while the pointer maths mapped clientX
  // across the full element width. Everything was offset by half the margin.
  // Measuring the host and using it as the viewBox width keeps the mapping 1:1.
  // A CALLBACK ref, not useRef + useEffect([]). The component early-returns
  // "no data yet" before this div exists, so on the first render there is no
  // node to observe — an effect with [] deps bails on the null ref and never
  // runs again, leaving the width stuck at its initial value once data finally
  // arrives. That is exactly how the first attempt at this fix failed. A
  // callback ref fires whenever the node actually mounts or unmounts.
  const roRef = useRef<ResizeObserver | null>(null);
  const [width, setWidth] = useState(720);
  const hostRef = useCallback((el: HTMLDivElement | null) => {
    roRef.current?.disconnect();
    roRef.current = null;
    if (!el) return;
    const apply = (w: number) => {
      const next = Math.max(320, Math.round(w));
      setWidth((prev) => (prev === next ? prev : next));
    };
    apply(el.getBoundingClientRect().width);
    if (typeof ResizeObserver === 'undefined') return;
    const ro = new ResizeObserver((entries) => apply(entries[0].contentRect.width));
    ro.observe(el);
    roRef.current = ro;
  }, []);

  const { xs, yTicks, scaleX, scaleY, domainY, unscaleX } = useMemo(() => {
    const allX = series.flatMap((s) => s.points.map((p) => p.x));
    const allY = series.flatMap((s) => s.points.map((p) => p.y)).filter((v): v is number => v !== null);

    const x0 = Math.min(...allX);
    const x1 = Math.max(...allX);
    const yHi = allY.length ? Math.max(...allY) : 1;
    const yLo = Math.min(yMin, allY.length ? Math.min(...allY) : 0);
    const top = yHi === yLo ? yLo + 1 : yHi * 1.08;

    const plotW = width - PAD.left - PAD.right;
    const plotH = height - PAD.top - PAD.bottom;

    return {
      xs: allX,
      domainY: [yLo, top] as const,
      yTicks: niceTicks(yLo, top),
      scaleX: (x: number) => PAD.left + (x1 === x0 ? 0 : ((x - x0) / (x1 - x0)) * plotW),
      scaleY: (y: number) => PAD.top + plotH - ((y - yLo) / (top - yLo)) * plotH,
      unscaleX: (px: number) => (x1 === x0 ? x0 : x0 + ((px - PAD.left) / plotW) * (x1 - x0)),
    };
  }, [series, height, yMin, width]);

  if (xs.length === 0) return <p className="empty">No data in this range yet.</p>;

  const spanMs = Math.max(...xs) - Math.min(...xs);

  /**
   * The point each series shows for the current cursor time — resolved per
   * series against its own points, so a series with different sampling (or a
   * failed sample the other series didn't have) still reports its own value.
   */
  const valueAt = (s: Series, t: number): { x: number; y: number } | null => {
    let best: { x: number; y: number } | null = null;
    let bestD = Infinity;
    for (const p of s.points) {
      if (p.y === null) continue;
      const d = Math.abs(p.x - t);
      if (d < bestD) {
        bestD = d;
        best = { x: p.x, y: p.y };
      }
    }
    // Don't attribute a reading to a time it doesn't belong to: past a quarter
    // of the visible span the nearest sample is not "the value here".
    if (best && spanMs > 0 && bestD > spanMs / 4) return null;
    return best;
  };

  return (
    <div className="chart-host" ref={hostRef}>
      {series.length > 1 && (
        <div className="legend">
          {series.map((s) => (
            <span className="key" key={s.label}>
              <span className="swatch" style={{ background: s.color }} aria-hidden />
              {s.label}
            </span>
          ))}
        </div>
      )}

      <svg
        viewBox={`0 0 ${width} ${height}`}
        width="100%"
        height={height}
        role="img"
        aria-label={`${series.map((s) => s.label).join(' and ')} over time`}
        onMouseLeave={() => setHover(null)}
        onMouseMove={(e) => {
          const svg = e.currentTarget;
          // Ask the browser for the real client->user-space transform rather
          // than deriving it from an assumed 1:1 mapping. getScreenCTM accounts
          // for viewBox scaling, preserveAspectRatio letterboxing, CSS
          // transforms and scroll — so the crosshair lands under the cursor
          // even if the measured width is momentarily stale (first paint, an
          // in-flight resize). Belt and braces: the measurement above makes the
          // chart fill its container, this makes the pointer correct
          // regardless.
          const ctm = svg.getScreenCTM();
          let raw: number;
          if (ctm) {
            raw = (e.clientX - ctm.e) / (ctm.a || 1);
          } else {
            const rect = svg.getBoundingClientRect();
            raw = ((e.clientX - rect.left) / (rect.width || 1)) * width;
          }
          const px = Math.min(width - PAD.right, Math.max(PAD.left, raw));
          // The crosshair tracks the cursor rather than snapping to the nearest
          // sample. Snapping is fine when samples are dense, but on a sparse
          // series (speed) the nearest sample can be hours away, so the line
          // jumps far from the pointer — which is what looked broken.
          // px: offset within .chart-host, for positioning the HTML tooltip.
          // Derived from client coords so it cannot drift from the cursor the
          // way a viewBox-percentage does when the two disagree.
          const host = svg.parentElement;
          const hostLeft = host ? host.getBoundingClientRect().left : svg.getBoundingClientRect().left;
          setHover({ x: px, t: unscaleX(px), px: e.clientX - hostLeft });
        }}
      >
        {yTicks.map((t) => (
          <g key={t}>
            {/* Solid hairlines — dashing reads as a threshold when it is just a grid. */}
            <line x1={PAD.left} x2={width - PAD.right} y1={scaleY(t)} y2={scaleY(t)} stroke="var(--gridline)" strokeWidth="1" />
            <text x={PAD.left - 8} y={scaleY(t) + 4} textAnchor="end" fontSize="11" fill="var(--text-muted)" style={{ fontVariantNumeric: 'tabular-nums' }}>
              {t >= 1000 ? `${(t / 1000).toFixed(1)}k` : t}
            </text>
          </g>
        ))}

        <line x1={PAD.left} x2={width - PAD.right} y1={scaleY(domainY[0])} y2={scaleY(domainY[0])} stroke="var(--baseline)" strokeWidth="1" />

        {series.map((s) => {
          // Break the path on nulls so a gap in the data reads as a gap, not as
          // a straight line implying measurements nobody took. A sparse series
          // is the exception — see the `sparse` doc on Series.
          const drawn = s.sparse ? s.points.filter((p) => p.y !== null) : s.points;
          const segments: string[] = [];
          let current = '';
          for (const p of drawn) {
            if (p.y === null) {
              if (current) segments.push(current);
              current = '';
              continue;
            }
            current += `${current ? 'L' : 'M'}${scaleX(p.x).toFixed(1)},${scaleY(p.y).toFixed(1)}`;
          }
          if (current) segments.push(current);
          return (
            <g key={s.label}>
              {segments.map((d, i) => (
                <path key={i} d={d} fill="none" stroke={s.color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
              ))}
              {/* Sparse samples are discrete events — mark each so the reader
                  sees them as occasional tests, not a continuous trace. */}
              {s.sparse &&
                drawn.map((p, i) => <circle key={`d${i}`} cx={scaleX(p.x)} cy={scaleY(p.y as number)} r="2.5" fill={s.color} />)}
            </g>
          );
        })}

        {hover && (
          <>
            <line x1={hover.x} x2={hover.x} y1={PAD.top} y2={height - PAD.bottom} stroke="var(--baseline)" strokeWidth="1" />
            {series.map((s) => {
              const p = valueAt(s, hover.t);
              if (!p) return null;
              return (
                <circle
                  key={s.label}
                  cx={scaleX(p.x)}
                  cy={scaleY(p.y)}
                  r="4"
                  fill={s.color}
                  stroke="var(--surface-1)"
                  strokeWidth="2"
                />
              );
            })}
          </>
        )}

        <text x={PAD.left} y={height - 6} fontSize="11" fill="var(--text-muted)">
          {fmtTime(Math.min(...xs), spanMs)}
        </text>
        <text x={width - PAD.right} y={height - 6} textAnchor="end" fontSize="11" fill="var(--text-muted)">
          {fmtTime(Math.max(...xs), spanMs)}
        </text>
        {yLabel && (
          <text x={PAD.left} y={PAD.top - 1} fontSize="11" fill="var(--text-muted)">
            {yLabel}
          </text>
        )}
      </svg>

      {hover && (
        <div className="tooltip" style={{ left: `${hover.px}px`, top: 0, transform: 'translateX(-50%)' }}>
          <div style={{ color: 'var(--text-muted)' , marginBottom: 2 }}>
            {fmtTime(series.some((s) => s.sparse) ? (valueAt(series[0], hover.t)?.x ?? hover.t) : hover.t, spanMs)}
          </div>
          {series.map((s) => {
            const p = valueAt(s, hover.t);
            return (
              <div key={s.label} style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                <span className="swatch" style={{ background: s.color }} aria-hidden />
                {s.label}: {p === null ? '—' : `${Math.round(p.y * 10) / 10}${s.unit}`}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

/**
 * Hour-of-day × day-of-week heatmap.
 *
 * Sequential single-hue ramp: magnitude only, light→dark, never a rainbow. Cells
 * with no coverage are drawn as the surface with a hairline ring rather than the
 * ramp's lightest step, so "nothing measured" cannot be misread as "near zero".
 */
export function Heatmap({
  cells,
  unit,
  label,
}: {
  cells: { day: number; hour: number; value: number | null }[];
  unit: string;
  label: string;
}) {
  const [hover, setHover] = useState<{ day: number; hour: number; value: number | null } | null>(null);
  const days = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
  const RAMP = ['var(--seq-100)', 'var(--seq-250)', 'var(--seq-400)', 'var(--seq-550)', 'var(--seq-700)'];

  const values = cells.map((c) => c.value).filter((v): v is number => v !== null);
  const max = values.length ? Math.max(...values) : 1;
  const min = values.length ? Math.min(...values) : 0;

  const cw = 26;
  const ch = 18;
  const left = 34;
  const top = 16;

  const colorFor = (v: number | null) => {
    if (v === null) return 'var(--surface-1)';
    const t = max === min ? 0.5 : (v - min) / (max - min);
    return RAMP[Math.min(RAMP.length - 1, Math.floor(t * RAMP.length))];
  };

  return (
    <div className="chart-host">
      <div className="legend">
        <span className="key">{label}: low</span>
        {RAMP.map((c) => (
          <span key={c} className="swatch" style={{ background: c }} aria-hidden />
        ))}
        <span className="key">high</span>
        <span className="key">
          <span className="swatch" style={{ background: 'var(--surface-1)', border: '1px solid var(--baseline)' }} aria-hidden />
          not measured
        </span>
      </div>

      <div className="scroll-x">
        <svg viewBox={`0 0 ${left + 24 * cw + 8} ${top + 7 * ch + 18}`} width={left + 24 * cw + 8} height={top + 7 * ch + 18} role="img" aria-label={`${label} by hour and weekday`}>
          {[0, 6, 12, 18, 23].map((h) => (
            <text key={h} x={left + h * cw + cw / 2} y={11} textAnchor="middle" fontSize="10" fill="var(--text-muted)">
              {h}
            </text>
          ))}
          {days.map((d, i) => (
            <text key={d} x={left - 6} y={top + i * ch + ch / 2 + 3} textAnchor="end" fontSize="10" fill="var(--text-muted)">
              {d}
            </text>
          ))}
          {cells.map((c) => (
            <rect
              key={`${c.day}-${c.hour}`}
              // 2px surface gap between fills rather than a stroke around each mark.
              x={left + c.hour * cw + 1}
              y={top + c.day * ch + 1}
              width={cw - 2}
              height={ch - 2}
              rx="3"
              fill={colorFor(c.value)}
              stroke={c.value === null ? 'var(--baseline)' : 'none'}
              strokeWidth="1"
              onMouseEnter={() => setHover(c)}
              onMouseLeave={() => setHover(null)}
            />
          ))}
        </svg>
      </div>

      {hover && (
        <div className="tooltip" style={{ left: 12, top: 0 }}>
          {days[hover.day]} {String(hover.hour).padStart(2, '0')}:00 —{' '}
          {hover.value === null ? 'not measured' : `${Math.round(hover.value * 10) / 10}${unit}`}
        </div>
      )}
    </div>
  );
}

export function Card({ title, subtitle, children }: { title: string; subtitle?: string; children: ReactNode }) {
  return (
    <section className="card">
      <h2>{title}</h2>
      {subtitle && <p className="sub">{subtitle}</p>}
      {children}
    </section>
  );
}
